import { ChartShapeInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import {
    ChartLayoutConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-layout-converter';
import _has from 'lodash/has';
import _set from 'lodash/set';
import { setUIValueToSdkData } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/util';
import {
    STDLblPos,
    ICTDLbl,
    ICTDLbls,
    DeltaOf,
    IEGExtensionDmlC,
    ICTExtensionDmlC,
    ICTLayout,
} from '@tencent/slide-sdk-model';
import {
    IChartExt,
    IChartExtension,
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IBaseDataLabel, IDataLabel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { IShapeModel } from '../../../Interface/shape-model.interface';
import { shallowOmit } from '@src/slide/infrastructure/module/independent/object/shallow-omit';

// 下面的"可选属性"都加了declare的原因是：
// 最近的master更新了Babel配置：allowDeclareFields: true，会导致class的可选属性被初始化为undefined。
// 在DataLabel的基类这些都是不希望被初始化（会有解构逻辑），所以增加declare声明
export class BaseDataLabel implements IBaseDataLabel {
    // 数据标签里是否展示图例名称
    declare public showLegendKey?: boolean;
    // 是否展示数值
    public showVal?: boolean;
    // 是否展示类别名称
    declare public showCategoryName?: boolean;
    // 是否展示系列名称
    declare public showSeriesName?: boolean;
    // 是否展示系列百分比（目前发现饼图中可以设置）
    declare public showPercent?: boolean;
    // 是否展示气泡大小
    declare public showBubbleSize?: boolean;
    // 标签位置，支持类型为LabelPositionType
    declare public labelPosition?: STDLblPos;
    // 标签内容的分割符，在配置展示图例形状、类别名称等中分隔。默认逗号
    declare public separator?: string;
    declare public props?: any;
    declare public paragraphList?: Array<any>;
    declare public textBodyStyle?: any;
    // 自定义标签内容
    declare public text?: any;
    // label自定义位置
    declare public layout?: IChartManualLayout;
    declare public numFmt?: any;
    declare public formatCode?: string;
    declare public ext?: IChartExt[];
    // 是否被删除了
    declare public delete?: boolean;
    public seriesId?: string;

    public constructor(dataLabel: ICTDLbl, seriesId?) {
        const {
            showLegendKey, showVal, showCatName,
            showSerName, showPercent, dLblPos,
            showBubbleSize, separator, tx,
            numFmt, deleteKeyword, layout,
            extLst,
        } = dataLabel;

        this.seriesId = seriesId;
        this.showLegendKey = showLegendKey?.val;
        this.showVal = showVal?.val;
        this.showCategoryName = showCatName?.val;
        this.showSeriesName = showSerName?.val;
        this.showPercent = showPercent?.val;
        this.labelPosition = dLblPos?.val;
        this.showBubbleSize = showBubbleSize?.val;
        this.separator = separator;
        this.numFmt = numFmt;
        this.delete = deleteKeyword?.val;

        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(dataLabel, this as unknown as IShapeModel);
        // 自定义标签文本
        if (tx?.rich) {
            this.text = {};
            ChartShapeInfoConverter.convertTxBodySdkToUi(tx.rich, this.text);
        }

        if (numFmt) {
            this.formatCode = numFmt.formatCode;
        }

        if (layout !== undefined) {
            this.layout = {};
            ChartLayoutConverter.convertPartialDataFromSdkToUi(layout, this.layout);
        }

        // dataLabel目前发现的w/h信息在extLst中，这里提取出extLst中的layout，用于ui数据合并
        this.ext = extLst?.ext?.map(ext => ({
            ...ext,
            egExtension: ext?.egExtensionDmlC?.map((extensionItem) => {
                if (extensionItem.layout) {
                    const uiLayout = {};
                    ChartLayoutConverter.convertPartialDataFromSdkToUi(extensionItem.layout, uiLayout);
                    const chartLayoutExt = { ...extensionItem, layout: uiLayout };

                    return chartLayoutExt as IChartExtension;
                }

                return extensionItem;
            }),
        }));
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering, complexity
    public static convertDataUiToSdk(uiData) {
        if (!uiData) {
            return uiData;
        }

        const sdkData: DeltaOf<ICTDLbl> = {};

        const { showLegendKey, showVal, showCategoryName, showSeriesName, showPercent, labelPosition,
            showBubbleSize, separator, numFmt, text, formatCode } = uiData || { };

        if (_has(uiData, 'showLegendKey')) {
            setUIValueToSdkData(sdkData, 'showLegendKey', showLegendKey);
        }
        if (_has(uiData, 'showVal')) {
            setUIValueToSdkData(sdkData, 'showVal', showVal);
        }
        if (_has(uiData, 'showCategoryName')) {
            setUIValueToSdkData(sdkData, 'showCatName', showCategoryName);
        }
        if (_has(uiData, 'showSeriesName')) {
            setUIValueToSdkData(sdkData, 'showSerName', showSeriesName);
        }
        if (_has(uiData, 'showPercent')) {
            setUIValueToSdkData(sdkData, 'showPercent', showPercent);
        }
        if (_has(uiData, 'labelPosition')) {
            setUIValueToSdkData(sdkData, 'dLblPos', labelPosition);
        }
        if (_has(uiData, 'showBubbleSize')) {
            setUIValueToSdkData(sdkData, 'showBubbleSize', showBubbleSize);
        }
        if (_has(uiData, 'separator')) {
            setUIValueToSdkData(sdkData, 'separator', separator, false);
        }
        if (_has(uiData, 'numFmt')) {
            setUIValueToSdkData(sdkData, 'numFmt', numFmt, false);
        }
        if (_has(uiData, 'delete')) {
            setUIValueToSdkData(sdkData, 'deleteKeyword', uiData.delete);
        }

        if (_has(uiData, 'text')) {
            if (uiData.text) {
                sdkData.tx = sdkData.tx ?? {};
                sdkData.tx.rich = sdkData.tx.rich ?? {};
                ChartShapeInfoConverter.convertTxBodyUiToSdk(text, sdkData.tx.rich);
            } else {
                sdkData.tx = null;
            }
        }

        if (_has(uiData, 'layout')) {
            if (uiData.layout) {
                sdkData.layout = sdkData.layout ?? {};
                ChartLayoutConverter.convertPartialDataFromUiToSdk(uiData.layout, sdkData.layout);
            } else {
                sdkData.layout = uiData.layout;
            }
        }

        if (_has(uiData, 'ext')) {
            if (uiData.ext) {
                this.convertExtFromUiToSdk(uiData, sdkData);
            } else {
                _set(sdkData, 'extLst.ext', uiData.ext);
            }
        }

        ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiData, sdkData);

        return sdkData;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private static convertExtFromUiToSdk(uiData, sdkData) {
        _set(sdkData, 'extLst.ext', uiData.ext.map((ext: IChartExt | undefined): ICTExtensionDmlC | undefined => {
            if (!ext) return ext;

            return {
                ...shallowOmit(ext, 'egExtension'),
                egExtensionDmlC: ext.egExtension?.map((extensionItem): IEGExtensionDmlC => {
                    if (!extensionItem?.layout) return extensionItem as IEGExtensionDmlC;

                    const sdkLayout = {};
                    ChartLayoutConverter.convertPartialDataFromUiToSdk(
                        extensionItem.layout as IChartManualLayout,
                        sdkLayout,
                    );

                    return {
                        ...extensionItem,
                        layout: sdkLayout as ICTLayout,
                    };
                }),
            };
        }));
    }
}

export class DataLabel extends BaseDataLabel implements IDataLabel {
    // 标识自己是ser中哪一个datalist下
    public dataId?: string;

    public constructor(props: ICTDLbl, seriesId: string, dataId?: string) {
        super(props, seriesId);
        this.dataId = dataId;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static convertDataUiToSdk(uiData) {
        return super.convertDataUiToSdk(uiData);
    }
}

export class DataLabels extends BaseDataLabel implements IDataLabel {
    public showLeaderLines: boolean;

    public constructor(dataLabels: ICTDLbls, seriesId?) {
        super(dataLabels, seriesId);

        const { showLeaderLines } = dataLabels;
        this.showLeaderLines = !!showLeaderLines?.val;

        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(dataLabels, this as unknown as IShapeModel);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static convertDataUiToSdk(uiData) {
        if (!uiData) {
            return uiData;
        }

        const sdkData: ICTDLbls = super.convertDataUiToSdk(uiData);

        if (_has(uiData, 'showLeaderLines')) {
            setUIValueToSdkData(sdkData, 'showLeaderLines', uiData.showLeaderLines);
        }

        return sdkData;
    }
}
